import { AlertTriangle, Bath, BedDouble, Building2, CalendarDays, ChevronDown, Compass, Home, Landmark, Layers3, LoaderCircle, RefreshCcw, Ruler, Sparkles, UploadCloud } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api, authConfig, formatApiErrorDetail } from "@/lib/api";
import { formatCurrency } from "@/lib/portalHelpers";

const DISTRICT_OPTIONS = [
  ["D01", "Marina / Raffles"], ["D02", "Tanjong Pagar"], ["D03", "Queenstown / Tiong Bahru"], ["D04", "Harbourfront / Telok Blangah"],
  ["D05", "Pasir Panjang / Clementi"], ["D06", "City Hall"], ["D07", "Bugis / Beach Road"], ["D08", "Little India"],
  ["D09", "Orchard"], ["D10", "Tanglin / Holland"], ["D11", "Newton / Novena"], ["D12", "Toa Payoh"],
  ["D13", "MacPherson"], ["D14", "Geylang / Eunos"], ["D15", "East Coast / Marine Parade"], ["D16", "Bedok"],
  ["D17", "Changi"], ["D18", "Tampines / Pasir Ris"], ["D19", "Hougang / Punggol"], ["D20", "Bishan / Thomson"],
  ["D21", "Upper Bukit Timah"], ["D22", "Jurong"], ["D23", "Bukit Batok / CCK"], ["D24", "Tengah"],
  ["D25", "Woodlands"], ["D26", "Mandai"], ["D27", "Yishun"], ["D28", "Seletar"],
].map(([code, label]) => ({ code, label, display: `${code} • ${label}` }));

const PRICE_TYPE_OPTIONS = ["Guide Price", "View to Offer", "Fixed"];
const PROPERTY_TYPE_OPTIONS = ["Condo", "HDB", "Landed", "Commercial"];
const TENURE_OPTIONS = ["Freehold", "99-year", "999-year", "Leasehold", "103-year", "110-year", "60-year"];
const BEDROOM_OPTIONS = ["1", "2", "3", "4", "5", "5+", "Study"];
const BATHROOM_OPTIONS = ["1", "2", "3", "4", "5+"];
const FLOOR_LEVEL_OPTIONS = ["Penthouse", "High", "Mid", "Low", "Ground"];
const FACING_OPTIONS = ["North", "South", "East", "West", "North-East", "North-West", "South-East", "South-West"];
const CONDITION_OPTIONS = ["Original", "Well-Maintained", "Renovated", "Brand New"];
const STATUS_OPTIONS = ["Active", "Under Offer", "Sold", "Draft"];
const BROCHURE_FALLBACK_URL = "https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf";

const optimizeCloudinaryUrl = (url, width = 1800) => (url?.includes("/upload/") ? url.replace("/upload/", `/upload/f_auto,q_auto,c_limit,w_${width}/`) : url);
const dashboardField = "w-full rounded-[20px] border border-[#001935]/10 bg-white px-4 py-3 text-sm text-[#001935] outline-none transition placeholder:text-[#8c8578] focus:border-[#D4AF37]/60 focus:ring-2 focus:ring-[#D4AF37]/20";
const sectionClass = "rounded-[30px] border border-[#001935]/10 bg-white/92 p-6 shadow-[0_20px_50px_rgba(0,25,53,0.06)]";
const fieldWellClass = "rounded-[22px] border border-[#001935]/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.95),rgba(250,248,241,0.92))] p-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.5)]";

const resolveDistrictDisplay = (value) => DISTRICT_OPTIONS.find((option) => option.code === String(value || "").toUpperCase())?.display || value || "";
const resolveDistrictCode = (value) => String(value || "").toUpperCase().match(/D\d{2}/)?.[0] || "";
const parseBedroomCount = (value) => ({ Study: 1, "5+": 5 }[value] || Number(value || 0) || 0);
const parseBathroomCount = (value) => ({ "5+": 5 }[value] || Number(value || 0) || 0);
const buildPostalMatchKey = (match) => [match.postal_code, match.block_house_number, match.street_name, match.latitude, match.longitude].filter(Boolean).join("::");
const makeDuplicateIdleState = (role) => ({ loading: false, has_match: false, blocking: false, can_override: role !== "agent", warning_message: "", agent_message: "", matches: [] });
const makePostalLookupIdleState = () => ({ loading: false, error: "", matches: [], selected_key: "", last_lookup_postal: "", prompt_choice: false });

const makeStudioForm = ({ currentUser, isAgentView, ownerOptions }) => ({
  project_name: "",
  block_house_number: "",
  unit_number: "",
  district_display: "",
  street_name: "",
  postal_code: "",
  asking_price: "",
  valuation_baseline: "",
  price_type: "Guide Price",
  monthly_maintenance_fees: "",
  property_type: "Condo",
  tenure: "Freehold",
  top_year: new Date().getFullYear(),
  size_built_up: "",
  size_land: "",
  bedroom_label: "3",
  bathroom_label: "2",
  floor_level: "High",
  facing: "",
  condition: "Well-Maintained",
  listing_headline: "",
  description: "",
  virtual_tour_url: "",
  video_walkthrough_url: "",
  admin_media: { primary_image: {}, gallery: [] },
  is_unique_gem: true,
  signal_strength: 4,
  assigned_agent_id: isAgentView ? currentUser.id : ownerOptions[0]?.id || "",
  status: "Active",
  override_duplicate_block: false,
  manual_address_override: false,
  latitude: null,
  longitude: null,
});

const mapListingToStudioForm = (listing, context) => ({
  ...makeStudioForm(context),
  project_name: listing.project_name || listing.title || "",
  block_house_number: listing.block_house_number || "",
  unit_number: listing.unit_number || "",
  district_display: resolveDistrictDisplay(listing.district || ""),
  street_name: listing.street_name || "",
  postal_code: listing.postal_code || "",
  asking_price: listing.price || "",
  valuation_baseline: listing.valuation_baseline || "",
  price_type: listing.price_type || "Guide Price",
  monthly_maintenance_fees: listing.monthly_maintenance_fees || "",
  property_type: listing.property_type || "Condo",
  tenure: listing.tenure || "Freehold",
  top_year: listing.top_year || listing.completion_year || new Date().getFullYear(),
  size_built_up: listing.size_built_up || listing.sqft || "",
  size_land: listing.size_land || "",
  bedroom_label: listing.bedroom_label || String(listing.bedrooms || "3"),
  bathroom_label: listing.bathroom_label || String(listing.bathrooms || "2"),
  floor_level: listing.floor_level || "High",
  facing: listing.facing || "",
  condition: listing.condition || "Well-Maintained",
  listing_headline: listing.listing_headline || listing.title || "",
  description: listing.description || "",
  virtual_tour_url: listing.virtual_tour_url || listing.tour_embed_url || "",
  video_walkthrough_url: listing.video_walkthrough_url || "",
  admin_media: listing.admin_media || { primary_image: {}, gallery: [] },
  is_unique_gem: Boolean(listing.is_unique_gem),
  signal_strength: listing.signal_strength || 4,
  assigned_agent_id: listing.owner_id || context.currentUser.id,
  status: listing.status || "Active",
  override_duplicate_block: false,
  manual_address_override: false,
  latitude: listing.latitude ?? null,
  longitude: listing.longitude ?? null,
});

const Section = ({ eyebrow, title, children, testId }) => (
  <section className={sectionClass} data-testid={testId}>
    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8b6d16]">{eyebrow}</p>
    <h2 className="mt-2 text-2xl font-semibold text-[#001935]">{title}</h2>
    <div className="mt-5 grid gap-4">{children}</div>
  </section>
);

const FieldLabel = ({ icon: Icon, label, required = false, testId }) => (
  <div className="mb-2 flex items-center gap-2 text-xs font-medium tracking-[0.04em] text-[#736a58]" data-testid={testId}>
    {Icon ? <Icon className="h-4 w-4 text-[#D4AF37]" /> : null}
    <span>
      {label}
      {required ? <span className="ml-1 font-bold text-[#D4AF37]">*</span> : null}
    </span>
  </div>
);

const SearchablePickerField = ({ icon, label, options, placeholder, required = false, testIdBase, value, onChange }) => {
  const normalizedOptions = options.map((option) => (typeof option === "string" ? { value: option, label: option } : option));
  const activeOption = normalizedOptions.find((option) => String(option.value) === String(value));
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(activeOption?.label || "");

  useEffect(() => {
    setQuery(activeOption?.label || "");
  }, [activeOption?.label]);

  const filteredOptions = normalizedOptions.filter((option) => option.label.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className={fieldWellClass} data-testid={`${testIdBase}-shell`}>
      <FieldLabel icon={icon} label={label} required={required} testId={`${testIdBase}-label`} />
      <div className="relative">
        <input
          className={`${dashboardField} pr-12`}
          data-testid={`${testIdBase}-input`}
          onBlur={() => window.setTimeout(() => { setOpen(false); setQuery(activeOption?.label || ""); }, 150)}
          onChange={(event) => { setQuery(event.target.value); setOpen(true); }}
          onFocus={() => { setOpen(true); setQuery(""); }}
          placeholder={placeholder}
          required={required}
          value={open ? query : activeOption?.label || query}
        />
        <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8b6d16]" />
        {open ? (
          <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-20 max-h-64 overflow-y-auto rounded-[20px] border border-[#D4AF37]/25 bg-white p-2 shadow-[0_18px_42px_rgba(0,25,53,0.12)]" data-testid={`${testIdBase}-menu`}>
            {filteredOptions.length ? filteredOptions.map((option) => (
              <button
                className="flex w-full items-center rounded-[14px] px-3 py-3 text-left text-sm text-[#001935] transition hover:bg-[#fff4db]"
                data-testid={`${testIdBase}-option-${String(option.value).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                key={option.value}
                onClick={() => { onChange(option.value); setQuery(option.label); setOpen(false); }}
                type="button"
              >
                {option.label}
              </button>
            )) : <div className="rounded-[14px] px-3 py-3 text-sm text-[#7a705d]" data-testid={`${testIdBase}-empty`}>No matching options found.</div>}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export const SovereignListingStudio = ({ currentUser, initialListingId, isAgentView, ownerOptions, onClearEditTarget, onSaved }) => {
  const context = useMemo(() => ({ currentUser, isAgentView, ownerOptions }), [currentUser, isAgentView, ownerOptions]);
  const [form, setForm] = useState(() => makeStudioForm(context));
  const [editingId, setEditingId] = useState("");
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [duplicateState, setDuplicateState] = useState(() => makeDuplicateIdleState(currentUser.role));
  const [districtMenuOpen, setDistrictMenuOpen] = useState(false);
  const [postalLookupState, setPostalLookupState] = useState(makePostalLookupIdleState);
  const duplicateTimer = useRef(null);
  const postalLookupTimer = useRef(null);
  const postalLookupRequestId = useRef(0);
  const latestContextRef = useRef(context);
  const previousInitialListingIdRef = useRef(initialListingId || "");

  useEffect(() => {
    latestContextRef.current = context;
  }, [context]);

  const resetStudio = useMemo(
    () => () => {
      setEditingId("");
      setForm(makeStudioForm(latestContextRef.current));
      setDistrictMenuOpen(false);
      setDuplicateState(makeDuplicateIdleState(currentUser.role));
      setPostalLookupState(makePostalLookupIdleState());
    },
    [currentUser.role],
  );

  useEffect(() => {
    const previousInitialListingId = previousInitialListingIdRef.current;
    previousInitialListingIdRef.current = initialListingId || "";

    if (!initialListingId) {
      if (previousInitialListingId) resetStudio();
      return;
    }

    const loadListing = async () => {
      setLoadingEdit(true);
      try {
        const response = await api.get(`/dashboard/listings/${initialListingId}`, authConfig());
        setEditingId(initialListingId);
        setForm(mapListingToStudioForm(response.data, latestContextRef.current));
        setDistrictMenuOpen(false);
        setDuplicateState(makeDuplicateIdleState(currentUser.role));
        setPostalLookupState(makePostalLookupIdleState());
      } catch (error) {
        toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to load this listing into the studio"));
      } finally {
        setLoadingEdit(false);
      }
    };
    loadListing();
  }, [currentUser.role, initialListingId, resetStudio]);

  useEffect(() => {
    if (isAgentView || editingId || form.assigned_agent_id || !ownerOptions.length) return;
    setForm((current) => ({ ...current, assigned_agent_id: ownerOptions[0]?.id || "" }));
  }, [editingId, form.assigned_agent_id, isAgentView, ownerOptions]);

  useEffect(() => {
    const projectName = String(form.project_name || "").trim();
    const unitNumber = String(form.unit_number || "").trim();
    if (duplicateTimer.current) window.clearTimeout(duplicateTimer.current);
    if (!projectName || !unitNumber) {
      setDuplicateState(makeDuplicateIdleState(currentUser.role));
      return undefined;
    }
    duplicateTimer.current = window.setTimeout(async () => {
      setDuplicateState((current) => ({ ...current, loading: true }));
      try {
        const response = await api.get("/dashboard/listings/preflight-duplicate", authConfig({ params: { project_name: projectName, unit_number: unitNumber, exclude_listing_id: editingId || undefined } }));
        setDuplicateState({ loading: false, ...response.data });
      } catch (error) {
        setDuplicateState(makeDuplicateIdleState(currentUser.role));
        toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to run duplicate detection"));
      }
    }, 320);
    return () => duplicateTimer.current && window.clearTimeout(duplicateTimer.current);
  }, [currentUser.role, editingId, form.project_name, form.unit_number]);

  const requestSignature = async () => (await api.get("/cloudinary/signature", authConfig({ params: { resource_type: "image", folder: "uploads/listings" } }))).data;

  const updateField = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const filteredDistricts = DISTRICT_OPTIONS.filter((option) => option.display.toLowerCase().includes(String(form.district_display || "").toLowerCase()) || option.code.toLowerCase().includes(String(form.district_display || "").toLowerCase()));
  const hasTrustedOneMapAddress = !form.manual_address_override && form.latitude != null && form.longitude != null && String(form.postal_code || "").length === 6;

  const applyPostalMatch = (match) => {
    setForm((current) => ({
      ...current,
      project_name: match.project_name || current.project_name,
      block_house_number: match.block_house_number || current.block_house_number,
      street_name: match.street_name || current.street_name,
      postal_code: match.postal_code || current.postal_code,
      manual_address_override: false,
      latitude: match.latitude,
      longitude: match.longitude,
    }));
    setPostalLookupState((current) => ({
      ...current,
      error: "",
      selected_key: buildPostalMatchKey(match),
      prompt_choice: false,
    }));
  };

  const lookupPostalCode = async (postalCode) => {
    const normalizedPostalCode = String(postalCode || "").replace(/\D/g, "").slice(0, 6);
    if (normalizedPostalCode.length !== 6) return;
    const requestId = postalLookupRequestId.current + 1;
    postalLookupRequestId.current = requestId;
    setPostalLookupState((current) => ({
      ...current,
      loading: true,
      error: "",
      last_lookup_postal: normalizedPostalCode,
      prompt_choice: false,
      matches: normalizedPostalCode === current.last_lookup_postal ? current.matches : [],
      selected_key: normalizedPostalCode === current.last_lookup_postal ? current.selected_key : "",
    }));

    try {
      const response = await api.get(`/listings/postal-lookup/${normalizedPostalCode}`, authConfig());
      if (postalLookupRequestId.current !== requestId) return;
      const matches = response.data.matches || [];
      if (!matches.length) {
        setPostalLookupState({ loading: false, error: "No OneMap result found for this postal code.", matches: [], selected_key: "", last_lookup_postal: normalizedPostalCode, prompt_choice: false });
        return;
      }

      if (matches.length === 1) {
        applyPostalMatch(matches[0]);
        setPostalLookupState({ loading: false, error: "", matches, selected_key: buildPostalMatchKey(matches[0]), last_lookup_postal: normalizedPostalCode, prompt_choice: false });
        return;
      }

      setPostalLookupState({ loading: false, error: "", matches, selected_key: "", last_lookup_postal: normalizedPostalCode, prompt_choice: true });
    } catch (error) {
      if (postalLookupRequestId.current !== requestId) return;
      setPostalLookupState({
        loading: false,
        error: formatApiErrorDetail(error.response?.data?.detail || "Unable to load OneMap address details"),
        matches: [],
        selected_key: "",
        last_lookup_postal: normalizedPostalCode,
        prompt_choice: false,
      });
    }
  };

  const handlePostalCodeChange = (event) => {
    const nextPostalCode = event.target.value.replace(/\D/g, "").slice(0, 6);
    if (postalLookupTimer.current) {
      window.clearTimeout(postalLookupTimer.current);
      postalLookupTimer.current = null;
    }

    setForm((current) => ({
      ...current,
      postal_code: nextPostalCode,
      manual_address_override: nextPostalCode === current.postal_code ? current.manual_address_override : false,
      latitude: nextPostalCode === current.postal_code ? current.latitude : null,
      longitude: nextPostalCode === current.postal_code ? current.longitude : null,
    }));

    if (nextPostalCode.length !== 6) {
      setPostalLookupState((current) => ({
        ...current,
        loading: false,
        error: "",
        matches: [],
        selected_key: "",
        prompt_choice: false,
        last_lookup_postal: "",
      }));
      return;
    }

    setPostalLookupState((current) => ({
      ...current,
      error: "",
      prompt_choice: false,
      selected_key: nextPostalCode === current.last_lookup_postal ? current.selected_key : "",
      matches: nextPostalCode === current.last_lookup_postal ? current.matches : [],
      last_lookup_postal: nextPostalCode === current.last_lookup_postal ? current.last_lookup_postal : "",
    }));

    if (postalLookupState.last_lookup_postal === nextPostalCode) return;

    postalLookupTimer.current = window.setTimeout(() => {
      lookupPostalCode(nextPostalCode);
    }, 260);
  };

  const updateAddressField = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: value,
      manual_address_override: true,
      latitude: null,
      longitude: null,
    }));
  };

  const handleManualAddressOverrideChange = (enabled) => {
    if (enabled) {
      setForm((current) => ({
        ...current,
        manual_address_override: true,
        latitude: null,
        longitude: null,
      }));
      setPostalLookupState((current) => ({
        ...current,
        loading: false,
        error: "",
        prompt_choice: false,
      }));
      return;
    }

    setForm((current) => ({ ...current, manual_address_override: false }));
    if (String(form.postal_code || "").length === 6) {
      lookupPostalCode(form.postal_code);
    }
  };

  const uploadFiles = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      const signaturePayload = await requestSignature();
      const uploads = [];
      for (const file of files) {
        const uploadPayload = new FormData();
        uploadPayload.append("file", file);
        uploadPayload.append("api_key", signaturePayload.api_key);
        uploadPayload.append("timestamp", String(signaturePayload.timestamp));
        uploadPayload.append("signature", signaturePayload.signature);
        uploadPayload.append("folder", signaturePayload.folder);
        const response = await fetch(`https://api.cloudinary.com/v1_1/${signaturePayload.cloud_name}/image/upload`, { method: "POST", body: uploadPayload });
        const asset = await response.json();
        if (!response.ok) throw new Error(asset.error?.message || "Cloudinary upload failed");
        uploads.push({ public_id: asset.public_id, secure_url: asset.secure_url, optimized_url: optimizeCloudinaryUrl(asset.secure_url, 1800), thumb_url: optimizeCloudinaryUrl(asset.secure_url, 480), uploaded_at: new Date().toISOString() });
      }
      setForm((current) => {
        const gallery = [...(current.admin_media?.gallery || []), ...uploads];
        return { ...current, admin_media: { primary_image: current.admin_media?.primary_image?.public_id ? current.admin_media.primary_image : uploads[0], gallery } };
      });
      toast.success("High-res media added to the sovereign listing draft");
    } catch (error) {
      toast.error(error.message || "Unable to upload listing media");
    } finally {
      setUploading(false);
    }
  };

  const removeAsset = (publicId) => setForm((current) => {
    const gallery = (current.admin_media?.gallery || []).filter((asset) => asset.public_id !== publicId);
    const primary = current.admin_media?.primary_image?.public_id === publicId ? gallery[0] || {} : current.admin_media?.primary_image || {};
    return { ...current, admin_media: { primary_image: primary, gallery } };
  });

  const setPrimaryAsset = (publicId) => setForm((current) => {
    const target = (current.admin_media?.gallery || []).find((asset) => asset.public_id === publicId);
    if (!target) return current;
    const gallery = [target, ...(current.admin_media?.gallery || []).filter((asset) => asset.public_id !== publicId)];
    return { ...current, admin_media: { primary_image: target, gallery } };
  });

  const generateDescription = async () => {
    if (!form.project_name || !resolveDistrictCode(form.district_display) || !form.asking_price || !form.size_built_up) {
      toast.error("Add project, district, asking price, and built-up size before generating Gemini copy.");
      return;
    }
    setAiBusy(true);
    try {
      const response = await api.post("/dashboard/listings/generate-description", {
        project_name: form.project_name,
        district: resolveDistrictCode(form.district_display),
        property_type: form.property_type,
        tenure: form.tenure,
        top_year: Number(form.top_year || 0),
        size_built_up: Number(form.size_built_up || 0),
        size_land: Number(form.size_land || 0),
        bedrooms: parseBedroomCount(form.bedroom_label),
        bedroom_label: form.bedroom_label,
        bathrooms: parseBathroomCount(form.bathroom_label),
        bathroom_label: form.bathroom_label,
        floor_level: form.floor_level,
        facing: form.facing,
        condition: form.condition,
        price: Number(form.asking_price || 0),
        valuation_baseline: Number(form.valuation_baseline || 0),
        price_type: form.price_type,
        monthly_maintenance_fees: Number(form.monthly_maintenance_fees || 0),
        listing_headline: form.listing_headline,
        is_unique_gem: form.is_unique_gem,
        signal_strength: Number(form.signal_strength || 3),
      }, authConfig());
      setForm((current) => ({ ...current, description: response.data.description }));
      toast.success("Gemini description drafted into the marketing field");
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to generate Gemini description"));
    } finally {
      setAiBusy(false);
    }
  };

  const buildPayload = () => {
    const district = resolveDistrictCode(form.district_display);
    const primaryImage = form.admin_media?.primary_image?.optimized_url || form.admin_media?.primary_image?.secure_url || form.admin_media?.gallery?.[0]?.optimized_url || form.admin_media?.gallery?.[0]?.secure_url || "";
    return {
      title: form.listing_headline || form.project_name,
      category: "buy",
      district,
      tenure: form.tenure,
      property_type: form.property_type,
      price: Number(form.asking_price || 0),
      sqft: Number(form.size_built_up || 0),
      bedrooms: parseBedroomCount(form.bedroom_label),
      bathrooms: parseBathroomCount(form.bathroom_label),
      address: [form.block_house_number, form.street_name, form.postal_code ? `Singapore ${form.postal_code}` : ""].filter(Boolean).join(" "),
      description: form.description,
      status: form.status,
      featured: Number(form.signal_strength || 0) >= 4,
      hidden: false,
      image_url: primaryImage,
      brochure_url: BROCHURE_FALLBACK_URL,
      tour_embed_url: form.virtual_tour_url,
      gallery_urls: (form.admin_media?.gallery || []).map((asset) => asset.optimized_url || asset.secure_url).filter(Boolean),
      floorplan_urls: [],
      tags: form.is_unique_gem ? ["Unique Gem"] : [],
      completion_year: Number(form.top_year || 0),
      admin_media: form.admin_media,
      project_name: form.project_name,
      block_house_number: form.block_house_number,
      unit_number: form.unit_number,
      street_name: form.street_name,
      postal_code: form.postal_code,
      valuation_baseline: Number(form.valuation_baseline || 0),
      price_type: form.price_type,
      monthly_maintenance_fees: Number(form.monthly_maintenance_fees || 0),
      top_year: Number(form.top_year || 0),
      size_built_up: Number(form.size_built_up || 0),
      size_land: Number(form.size_land || 0),
      bedroom_label: form.bedroom_label,
      bathroom_label: form.bathroom_label,
      floor_level: form.floor_level,
      facing: form.facing,
      condition: form.condition,
      listing_headline: form.listing_headline,
      virtual_tour_url: form.virtual_tour_url,
      video_walkthrough_url: form.video_walkthrough_url,
      is_unique_gem: form.is_unique_gem,
      signal_strength: Number(form.signal_strength || 3),
      assigned_agent_id: isAgentView ? currentUser.id : form.assigned_agent_id,
      owner_id: isAgentView ? currentUser.id : form.assigned_agent_id,
      is_sovereign: true,
      storage_table: "sovereign",
      override_duplicate_block: Boolean(form.override_duplicate_block),
      latitude: form.manual_address_override ? null : form.latitude,
      longitude: form.manual_address_override ? null : form.longitude,
    };
  };

  const submitListing = async (event) => {
    event.preventDefault();
    if (!resolveDistrictCode(form.district_display)) {
      toast.error("Please choose a valid district from D01 to D28.");
      return;
    }
    if (duplicateState.blocking) {
      toast.error(duplicateState.agent_message || "This unit is already managed in the Signal Network.");
      return;
    }
    if (duplicateState.has_match && duplicateState.can_override && !form.override_duplicate_block) {
      toast.error("Enable the admin override to deploy this as a secondary signal.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = buildPayload();
      const response = editingId
        ? await api.patch(`/listings/${editingId}`, payload, authConfig())
        : await api.post("/listings", payload, authConfig());
      toast.success(editingId ? "Sovereign listing updated successfully" : "Sovereign listing deployed successfully");
      setEditingId(response.data.id);
      setForm(mapListingToStudioForm(response.data, context));
      onSaved?.(response.data);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail || "Unable to deploy sovereign listing"));
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = !submitting && !uploading && !aiBusy && !duplicateState.blocking && (!duplicateState.has_match || !duplicateState.can_override || form.override_duplicate_block);

  return (
    <div className="grid gap-6" data-testid="sovereign-listing-studio">
      <div className="grid gap-4 lg:grid-cols-3" data-testid="sovereign-listing-studio-top-rail">
        <div className={sectionClass} data-testid="sovereign-listing-summary-card">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8b6d16]">Signal preview</p>
          <p className="mt-3 text-2xl font-semibold text-[#001935]">{form.asking_price ? formatCurrency(form.asking_price) : "Awaiting price"}</p>
          <p className="mt-3 text-sm leading-7 text-[#42566f]">{form.price_type} • Signal {form.signal_strength}/5 • {form.is_unique_gem ? "Unique Gem" : "Standard signal"}</p>
        </div>
        <div className={sectionClass} data-testid="sovereign-listing-summary-meta">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8b6d16]">Identity snapshot</p>
          <p className="mt-3 text-xl font-semibold text-[#001935]">{form.listing_headline || form.project_name || "Untitled sovereign listing"}</p>
          <p className="mt-3 text-sm leading-7 text-[#42566f]">{resolveDistrictCode(form.district_display) || "District pending"} • {form.property_type} • {form.tenure}</p>
        </div>
        <div className={sectionClass} data-testid="sovereign-listing-summary-deploy-note">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8b6d16]">Deployment trigger</p>
          <p className="mt-3 text-sm leading-7 text-[#42566f]">Deploying this sovereign listing updates dashboard inventory immediately and makes the listing eligible for the searchable vault / landing-page signal layer when Active.</p>
        </div>
      </div>

      <form className="grid gap-6" data-testid="sovereign-listing-form" onSubmit={submitListing}>
        <Section eyebrow="Create Listing" testId="sovereign-listing-identity-section" title={editingId ? "Edit Sovereign Listing" : "Deploy a new Sovereign Listing"}>
          <div className="flex flex-wrap items-start justify-between gap-3 rounded-[24px] border border-[#D4AF37]/25 bg-[#fffaf1] px-4 py-4">
            <div>
              <p className="text-sm font-semibold text-[#001935]" data-testid="sovereign-listing-studio-mode">{editingId ? "Editing existing sovereign signal" : "New sovereign signal"}</p>
              <p className="mt-1 text-sm leading-7 text-[#5f5a4e]">Unit Number triggers duplicate detection immediately, and deploying indexes the property into the searchable vault plus the landing-page signal layer.</p>
            </div>
            <div className="flex gap-2">
              <button className="outline-button !px-4 !py-2" data-testid="sovereign-listing-reset-button" onClick={() => { resetStudio(); onClearEditTarget?.(); }} type="button">
                <RefreshCcw className="mr-2 h-4 w-4" /> Reset
              </button>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className={fieldWellClass}>
              <FieldLabel label="Project Name" required testId="sovereign-listing-project-name-label" />
              <input className={dashboardField} data-testid="sovereign-listing-project-name-input" onChange={(event) => updateField("project_name", event.target.value)} placeholder="e.g. Amber Residences" required value={form.project_name} />
            </div>
            <div className={fieldWellClass}>
              <FieldLabel label="Block / House Number" testId="sovereign-listing-block-number-label" />
              <input className={`${dashboardField} ${hasTrustedOneMapAddress ? "cursor-not-allowed bg-[#f6f2e8] text-[#7b7467]" : ""}`} data-testid="sovereign-listing-block-number-input" disabled={hasTrustedOneMapAddress} onChange={(event) => updateAddressField("block_house_number", event.target.value)} placeholder="e.g. 12" value={form.block_house_number} />
            </div>
            <div className={fieldWellClass}>
              <FieldLabel label="Unit Number (Private)" required testId="sovereign-listing-unit-number-label" />
              <input className={dashboardField} data-testid="sovereign-listing-unit-number-input" onChange={(event) => updateField("unit_number", event.target.value)} placeholder="e.g. #19-01" required value={form.unit_number} />
              <p className="mt-2 text-xs text-[#7a705d]" data-testid="sovereign-listing-unit-number-note">Private field — used for duplicate detection only.</p>
            </div>
            <div className={`${fieldWellClass} relative`}>
              <FieldLabel label="District" testId="sovereign-listing-district-label" />
              <input
                className={`${dashboardField} pr-12`}
                data-testid="sovereign-listing-district-input"
                onBlur={() => window.setTimeout(() => setDistrictMenuOpen(false), 150)}
                onChange={(event) => { updateField("district_display", event.target.value); setDistrictMenuOpen(true); }}
                onFocus={() => setDistrictMenuOpen(true)}
                placeholder="Search district D01–D28"
                required
                value={form.district_display}
              />
              <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8b6d16]" />
              {districtMenuOpen ? (
                <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-20 max-h-64 overflow-y-auto rounded-[20px] border border-[#D4AF37]/25 bg-white p-2 shadow-[0_18px_42px_rgba(0,25,53,0.12)]" data-testid="sovereign-listing-district-menu">
                  {filteredDistricts.length ? filteredDistricts.map((option) => (
                    <button
                      className="flex w-full items-center rounded-[14px] px-3 py-3 text-left text-sm text-[#001935] transition hover:bg-[#fff4db]"
                      data-testid={`sovereign-listing-district-option-${option.code.toLowerCase()}`}
                      key={option.code}
                      onClick={() => { updateField("district_display", option.display); setDistrictMenuOpen(false); }}
                      type="button"
                    >
                      {option.display}
                    </button>
                  )) : <div className="rounded-[14px] px-3 py-3 text-sm text-[#7a705d]" data-testid="sovereign-listing-district-empty">No matching districts found.</div>}
                </div>
              ) : null}
            </div>
            <div className={fieldWellClass}>
              <FieldLabel label="Street Name" testId="sovereign-listing-street-name-label" />
              <input className={`${dashboardField} ${hasTrustedOneMapAddress ? "cursor-not-allowed bg-[#f6f2e8] text-[#7b7467]" : ""}`} data-testid="sovereign-listing-street-name-input" disabled={hasTrustedOneMapAddress} onChange={(event) => updateAddressField("street_name", event.target.value)} placeholder="e.g. Amber Road" value={form.street_name} />
            </div>
            <div className={fieldWellClass}>
              <FieldLabel label="Postal Code" testId="sovereign-listing-postal-code-label" />
              <input
                className={dashboardField}
                data-testid="sovereign-listing-postal-code-input"
                maxLength={6}
                onChange={handlePostalCodeChange}
                placeholder="e.g. 439874"
                value={form.postal_code}
              />
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-[#7a705d]" data-testid="sovereign-listing-postal-code-status-row">
                <span data-testid="sovereign-listing-postal-code-status-copy">
                  {postalLookupState.loading ? "Looking up OneMap now…" : form.postal_code?.length === 6 ? "OneMap auto-search runs immediately at 6 digits." : "Enter a 6-digit Singapore postal code to auto-fill the address."}
                </span>
                {postalLookupState.loading ? <LoaderCircle className="h-4 w-4 animate-spin text-[#8b6d16]" data-testid="sovereign-listing-postal-code-loading-icon" /> : null}
              </div>
              <div className="mt-3 rounded-[20px] border border-[#001935]/10 bg-[#fcfaf5] px-4 py-3" data-testid="sovereign-listing-address-trust-row">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-3 py-1 text-[11px] font-bold tracking-[0.06em] ${hasTrustedOneMapAddress ? "border border-emerald-200 bg-emerald-50 text-emerald-700" : form.manual_address_override ? "border border-[#D4AF37]/30 bg-[#fff4db] text-[#8b6d16]" : "border border-[#001935]/10 bg-white text-[#42566f]"}`} data-testid="sovereign-listing-address-trust-pill">
                      {hasTrustedOneMapAddress ? "Address confirmed from OneMap" : form.manual_address_override ? "Using manual address override" : "Address awaiting OneMap confirmation"}
                    </span>
                    <p className="text-xs leading-6 text-[#5f5a4e]" data-testid="sovereign-listing-address-trust-copy">
                      {hasTrustedOneMapAddress ? "Street and block are locked because this address is trusted from OneMap." : form.manual_address_override ? "Street and block are now editable. Coordinates will refresh after the next valid postal lookup." : "Enter a valid postal code or switch on manual override if you need to type the address yourself."}
                    </p>
                  </div>
                  <label className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.14em] text-[#42566f]" data-testid="sovereign-listing-manual-override-toggle-row">
                    <input checked={form.manual_address_override} data-testid="sovereign-listing-manual-override-toggle" onChange={(event) => handleManualAddressOverrideChange(event.target.checked)} type="checkbox" />
                    Manually edit address
                  </label>
                </div>
              </div>
              {postalLookupState.error ? <p className="mt-2 text-xs font-medium text-red-600" data-testid="sovereign-listing-postal-code-error">{postalLookupState.error}</p> : null}
              {postalLookupState.prompt_choice ? (
                <div className="mt-3 rounded-[20px] border border-[#D4AF37]/25 bg-[#fffaf1] p-3" data-testid="sovereign-listing-postal-match-chooser">
                  <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]" data-testid="sovereign-listing-postal-match-chooser-title">Choose the best OneMap match</p>
                  <div className="mt-3 grid gap-2">
                    {postalLookupState.matches.map((match, index) => {
                      const matchKey = buildPostalMatchKey(match);
                      return (
                        <button
                          className="rounded-[16px] border border-[#001935]/10 bg-white px-4 py-3 text-left transition hover:border-[#D4AF37]/45 hover:bg-[#fff7e6]"
                          data-testid={`sovereign-listing-postal-match-option-${index + 1}`}
                          key={matchKey}
                          onClick={() => applyPostalMatch(match)}
                          type="button"
                        >
                          <p className="text-sm font-semibold text-[#001935]">{match.project_name || match.search_value}</p>
                          <p className="mt-1 text-xs text-[#5f5a4e]">{match.address || [match.block_house_number, match.street_name, match.postal_code].filter(Boolean).join(" ")}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
              {form.latitude != null && form.longitude != null ? (
                <div className="mt-3 rounded-[18px] border border-emerald-200 bg-emerald-50 px-4 py-3 text-xs text-emerald-800" data-testid="sovereign-listing-postal-geocode-summary">
                  <p className="font-semibold" data-testid="sovereign-listing-postal-geocode-summary-title">OneMap address linked</p>
                  <p className="mt-1" data-testid="sovereign-listing-postal-geocode-summary-copy">
                    {form.project_name || "Project name pending"} • {form.block_house_number || "Block pending"} {form.street_name || "Street pending"} • Lat {Number(form.latitude).toFixed(6)}, Lng {Number(form.longitude).toFixed(6)}
                  </p>
                </div>
              ) : null}
            </div>
          </div>

          {duplicateState.loading ? <div className="rounded-[22px] border border-[#D4AF37]/25 bg-[#fffaf1] px-4 py-4 text-sm text-[#5c4921]" data-testid="sovereign-duplicate-loading"><LoaderCircle className="mr-2 inline h-4 w-4 animate-spin" />Checking the Signal Network for duplicates...</div> : null}
          {duplicateState.has_match ? (
            <div className={`rounded-[24px] border px-4 py-4 ${duplicateState.blocking ? "border-red-200 bg-red-50 text-red-700" : "border-[#D4AF37]/30 bg-[#fff4db] text-[#6b4d12]"}`} data-testid="sovereign-duplicate-alert">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold" data-testid="sovereign-duplicate-alert-message">{duplicateState.blocking ? duplicateState.agent_message : duplicateState.warning_message}</p>
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="sovereign-duplicate-match-list">
                    {duplicateState.matches.map((match) => (
                      <span className="rounded-full border border-current/15 bg-white/70 px-3 py-2 text-xs font-semibold" data-testid={`sovereign-duplicate-match-${match.listing_id}`} key={match.listing_id}>
                        {match.listing_title} · {match.owner_name || "Agent"} · {match.status || "Active"}
                      </span>
                    ))}
                  </div>
                  {duplicateState.can_override ? (
                    <label className="mt-4 flex items-start gap-3 rounded-[20px] border border-[#001935]/10 bg-white px-4 py-3 text-sm text-[#42566f]" data-testid="sovereign-duplicate-override-row">
                      <input checked={form.override_duplicate_block} data-testid="sovereign-duplicate-override-checkbox" onChange={(event) => updateField("override_duplicate_block", event.target.checked)} type="checkbox" />
                      <span>Admin override: deploy this anyway as a secondary signal in the network.</span>
                    </label>
                  ) : null}
                </div>
              </div>
            </div>
          ) : null}
        </Section>

        <Section eyebrow="Financials" testId="sovereign-listing-financials-section" title="Financial positioning">
          <div className="grid gap-4 md:grid-cols-2">
            <div className={fieldWellClass}>
              <FieldLabel label="Asking Price" required testId="sovereign-listing-asking-price-label" />
              <input className={dashboardField} data-testid="sovereign-listing-asking-price-input" min="0" onChange={(event) => updateField("asking_price", event.target.value)} placeholder="e.g. 2180000" required type="number" value={form.asking_price} />
            </div>
            <div className={fieldWellClass}>
              <FieldLabel label="Valuation Baseline" testId="sovereign-listing-valuation-baseline-label" />
              <input className={dashboardField} data-testid="sovereign-listing-valuation-baseline-input" min="0" onChange={(event) => updateField("valuation_baseline", event.target.value)} placeholder="e.g. 2100000" type="number" value={form.valuation_baseline} />
            </div>
            <div className={fieldWellClass}>
              <FieldLabel label="Price Type" testId="sovereign-listing-price-type-label" />
              <select className={dashboardField} data-testid="sovereign-listing-price-type-select" onChange={(event) => updateField("price_type", event.target.value)} value={form.price_type}>{PRICE_TYPE_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}</select>
            </div>
            <div className={fieldWellClass}>
              <FieldLabel label="Monthly Maintenance Fees" testId="sovereign-listing-maintenance-fees-label" />
              <input className={dashboardField} data-testid="sovereign-listing-maintenance-fees-input" min="0" onChange={(event) => updateField("monthly_maintenance_fees", event.target.value)} placeholder="e.g. 480" type="number" value={form.monthly_maintenance_fees} />
            </div>
          </div>
        </Section>

        <Section eyebrow="Specs" testId="sovereign-listing-specs-section" title="Property data signal">
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            <SearchablePickerField icon={Building2} label="Property Type" onChange={(value) => updateField("property_type", value)} options={PROPERTY_TYPE_OPTIONS} placeholder="Search property type" testIdBase="sovereign-listing-property-type" value={form.property_type} />
            <SearchablePickerField icon={Landmark} label="Tenure" onChange={(value) => updateField("tenure", value)} options={TENURE_OPTIONS} placeholder="Search tenure" testIdBase="sovereign-listing-tenure" value={form.tenure} />
            <SearchablePickerField icon={CalendarDays} label="TOP Year (Completion)" onChange={(value) => updateField("top_year", value)} options={Array.from({ length: 75 }, (_, index) => 2034 - index).filter((year) => year >= 1960).map((year) => ({ value: year, label: String(year) }))} placeholder="Search TOP year" testIdBase="sovereign-listing-top-year" value={form.top_year} />
            <div className={fieldWellClass}>
              <FieldLabel icon={Ruler} label="Total Size (Sqft)" required testId="sovereign-listing-built-up-label" />
              <input className={dashboardField} data-testid="sovereign-listing-built-up-input" min="0" onChange={(event) => updateField("size_built_up", event.target.value)} placeholder="e.g. 1,205" required type="number" value={form.size_built_up} />
            </div>
            <div className={fieldWellClass}>
              <FieldLabel icon={Home} label="Land Size (Sqft)" testId="sovereign-listing-land-size-label" />
              <input className={dashboardField} data-testid="sovereign-listing-land-size-input" min="0" onChange={(event) => updateField("size_land", event.target.value)} placeholder="Optional for landed homes" type="number" value={form.size_land} />
            </div>
            <div className={fieldWellClass}>
              <FieldLabel icon={BedDouble} label="Bedrooms" testId="sovereign-listing-bedroom-label" />
              <select className={dashboardField} data-testid="sovereign-listing-bedroom-select" onChange={(event) => updateField("bedroom_label", event.target.value)} value={form.bedroom_label}>{BEDROOM_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}</select>
            </div>
            <div className={fieldWellClass}>
              <FieldLabel icon={Bath} label="Bathrooms" testId="sovereign-listing-bathroom-label" />
              <select className={dashboardField} data-testid="sovereign-listing-bathroom-select" onChange={(event) => updateField("bathroom_label", event.target.value)} value={form.bathroom_label}>{BATHROOM_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}</select>
            </div>
            <SearchablePickerField icon={Layers3} label="Floor Level" onChange={(value) => updateField("floor_level", value)} options={FLOOR_LEVEL_OPTIONS} placeholder="Search floor level" testIdBase="sovereign-listing-floor-level" value={form.floor_level} />
            <SearchablePickerField icon={Compass} label="Unit Facing" onChange={(value) => updateField("facing", value)} options={FACING_OPTIONS} placeholder="Search unit facing" testIdBase="sovereign-listing-facing" value={form.facing} />
            <SearchablePickerField icon={Home} label="Condition" onChange={(value) => updateField("condition", value)} options={CONDITION_OPTIONS} placeholder="Search condition" testIdBase="sovereign-listing-condition" value={form.condition} />
          </div>
        </Section>

        <Section eyebrow="Marketing" testId="sovereign-listing-marketing-section" title="Institutional marketing suite">
          <input className={dashboardField} data-testid="sovereign-listing-headline-input" onChange={(event) => updateField("listing_headline", event.target.value)} placeholder="Listing Headline" required value={form.listing_headline} />
          <div className="grid gap-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm font-semibold text-[#001935]" data-testid="sovereign-listing-description-label">Gemini AI Description</p>
              <button className="gold-button !px-4 !py-2 text-sm" data-testid="sovereign-listing-gemini-generate-button" disabled={aiBusy} onClick={generateDescription} type="button">
                {aiBusy ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} Generate Gemini AI Description
              </button>
            </div>
            <textarea className={`${dashboardField} min-h-[220px]`} data-testid="sovereign-listing-description-input" onChange={(event) => updateField("description", event.target.value)} placeholder="Institutional-grade description" required value={form.description} />
          </div>
          <label className="rounded-[24px] border border-dashed border-[#D4AF37]/35 bg-[#fffaf1] px-4 py-4 text-sm text-[#42566f]" data-testid="sovereign-listing-media-upload-zone">
            <span className="flex items-center text-sm font-semibold text-[#001935]"><UploadCloud className="mr-2 h-4 w-4 text-[#8b6d16]" /> High-res photo gallery (multi-file drag & drop)</span>
            <input className="mt-3 block w-full" data-testid="sovereign-listing-media-input" multiple onChange={(event) => uploadFiles(Array.from(event.target.files || []))} type="file" />
            <p className="mt-2 text-xs text-[#7a705d]">Upload premium images before deployment — the first image becomes the hero by default.</p>
          </label>
          {(form.admin_media?.gallery || []).length ? (
            <div className="grid gap-3 md:grid-cols-3" data-testid="sovereign-listing-media-gallery">
              {(form.admin_media?.gallery || []).map((asset) => (
                <div className="rounded-[22px] border border-[#001935]/10 bg-white p-3" data-testid={`sovereign-listing-media-card-${asset.public_id}`} key={asset.public_id}>
                  <img alt="Listing upload preview" className="aspect-[4/3] w-full rounded-[16px] object-cover" src={asset.thumb_url || asset.secure_url} />
                  <div className="mt-3 flex gap-2">
                    <button className="outline-button !px-3 !py-2 !text-xs" data-testid={`sovereign-listing-set-primary-${asset.public_id}`} onClick={() => setPrimaryAsset(asset.public_id)} type="button">Set Hero</button>
                    <button className="outline-button !px-3 !py-2 !text-xs" data-testid={`sovereign-listing-remove-media-${asset.public_id}`} onClick={() => removeAsset(asset.public_id)} type="button">Remove</button>
                  </div>
                </div>
              ))}
            </div>
          ) : null}
          <input className={dashboardField} data-testid="sovereign-listing-virtual-tour-input" onChange={(event) => updateField("virtual_tour_url", event.target.value)} placeholder="Virtual Tour URL (Matterport / 3D)" value={form.virtual_tour_url} />
          <input className={dashboardField} data-testid="sovereign-listing-video-url-input" onChange={(event) => updateField("video_walkthrough_url", event.target.value)} placeholder="Video Walkthrough URL (YouTube / Vimeo)" value={form.video_walkthrough_url} />
        </Section>

        <Section eyebrow="Controls" testId="sovereign-listing-controls-section" title="Sovereign controls">
          <div className="grid gap-4 md:grid-cols-2">
            <label className="rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] px-4 py-4 text-sm text-[#42566f]" data-testid="sovereign-listing-unique-gem-toggle-row">
              <span className="mb-3 block text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Mark as Unique Gem</span>
              <div className="flex items-center gap-3"><input checked={form.is_unique_gem} data-testid="sovereign-listing-unique-gem-toggle" onChange={(event) => updateField("is_unique_gem", event.target.checked)} type="checkbox" /> Unique Gem</div>
            </label>
            <div className="rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] px-4 py-4 text-sm text-[#42566f]" data-testid="sovereign-listing-signal-strength-row">
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Signal Strength</span>
                <span className="text-sm font-semibold text-[#001935]" data-testid="sovereign-listing-signal-strength-value">{form.signal_strength}/5</span>
              </div>
              <input className="mt-4 w-full accent-[#D4AF37]" data-testid="sovereign-listing-signal-strength-slider" max="5" min="1" onChange={(event) => updateField("signal_strength", event.target.value)} type="range" value={form.signal_strength} />
            </div>
            {!isAgentView ? (
              <SearchablePickerField
                icon={Building2}
                label="Assigned Agent"
                onChange={(value) => updateField("assigned_agent_id", value)}
                options={ownerOptions.map((owner) => ({ value: owner.id, label: `${owner.name} · /agent/${owner.subdomain_slug || "profile"}` }))}
                placeholder="Search assigned agent"
                required
                testIdBase="sovereign-listing-assigned-agent"
                value={form.assigned_agent_id}
              />
            ) : null}
            <select className={dashboardField} data-testid="sovereign-listing-status-select" onChange={(event) => updateField("status", event.target.value)} value={form.status}>{STATUS_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}</select>
          </div>
          <button className="gold-button w-full" data-testid="sovereign-listing-submit-button" disabled={!canSubmit} type="submit">
            {submitting || loadingEdit || uploading ? <LoaderCircle className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} {editingId ? "Update Sovereign Listing" : "Deploy Sovereign Listing"}
          </button>
        </Section>
      </form>
    </div>
  );
};